import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  toPascalCase,
  UrlLinks,
  useDeviceType,
  DeviceTypes,
  UserDetails,
  NavigateConst,
  getDecryptedValue,
  apiResponseSafeCheck,
  shouldDisplayGetHealthyMenu,
  useProxyRead,
  hasActiveDesignation,
} from 'common';
import { DashboardLinks } from 'pages';
import { UserInitialsAvatar } from 'organisms';

import { GlobalContext, isAvMed } from 'context';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  UmbrellaIcon,
  MoneyIcon,
  StethoscopeIcon,
  AppleIcon,
  ProfileCard,
  AccordionWithNoIcon,
} from '@sentara-health/optimahealth_web';

/**
 * Sides menu
 * @returns
 */
function SideMenu() {
  const deviceType = useDeviceType();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;  
  const { t } = useTranslation();
  const profileUrl = window.location.pathname !== NavigateConst.profile
  const {
    getFeature,
    planName,
    loginMemberId,
    serviceNumber,
    profileState,
    approvedDesignators,
    sharedAccessRefreshKey,
  } = useContext(GlobalContext);
  const navigate = useNavigate();
  const {
    benefitsUrl,
    paymentCalims,
    doctorsMedication,
    getHealthy,
    guestBenefitsUrl,
    memberProfileUrl,
    guestWellnessUrl,
  }: any = UrlLinks();
  const [getPhone, setPhone] = useState<any>();
  const [planList, setPlanList] = useState('');
  const [personalInfo, setPersonalInfo] = useState<any>({});
  const [isPersonalInfoResolved, setIsPersonalInfoResolved] = useState(false);
  const [showSharedAccounts, setShowSharedAccounts] = useState(false);
  const { getProxyRead } = useProxyRead();
  /* Determine getting memberlist by passing dependents and set profile name */
  useEffect(() => {
    setPlanList(planName ?? '');
  }, [planName]);

  /* Determine profile image api call */
  useEffect(() => {
    (async () => {
      const personalDetails = await getDecryptedValue(UserDetails.guest);

      const personalInfo = JSON.parse(personalDetails ?? '{}');
      setPersonalInfo(personalInfo);
      setIsPersonalInfoResolved(true);

      if (getFeature?.DesignatedRepresentativeTab && personalInfo?.isGuest) {
        const readResponse = await getProxyRead();
        setShowSharedAccounts(hasActiveDesignation(readResponse));
      } else {
        setShowSharedAccounts(false);
      }
    })();
  }, [getFeature?.DesignatedRepresentativeTab, getProxyRead]);

  // Reveal the shared-accounts link as soon as the guest gains an approved designation, without a reload.
  useEffect(() => {
    if ((approvedDesignators?.length ?? 0) > 0) {
      setShowSharedAccounts(true);
    }
  }, [approvedDesignators]);

  // Re-read shared-access state after a guest proxy action (accept/deny/dismiss) so the nav link reflects it without a reload.
  const sharedAccessKeyInitialized = useRef(false);
  useEffect(() => {
    if (!sharedAccessKeyInitialized.current) {
      sharedAccessKeyInitialized.current = true;
      return;
    }
    (async () => {
      if (getFeature?.DesignatedRepresentativeTab && personalInfo?.isGuest) {
        const readResponse = await getProxyRead();
        setShowSharedAccounts(hasActiveDesignation(readResponse));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedAccessRefreshKey]);

  useEffect(() => {
    if (apiResponseSafeCheck(serviceNumber)) {
      const getPhone = JSON.parse(serviceNumber ?? '{}');
      setPhone(getPhone?.data);
    }
  }, [serviceNumber]);

  const memberServices = (
    <div>
      <div> {getPhone?.benefitPhone1800}</div>
      <div>{getPhone?.benefitPhoneLocal}</div>
    </div>
  );
  const handleAccordionToggleItem = (index: number) => {
    return index; // Set the initial active index here
  };

  const navigateViewDesignated = () => {
    navigate(NavigateConst.guestSharedAccounts, {
      state: {
        forceSharedTableView: true,
        navTs: Date.now(),
      },
    });
  };

  return (
    <>
      {isDesktop && (
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12 col-12">
            <div className="heading-with-bg mb-3 mt-3">
              <AccordionWithNoIcon
                id="myAccount"
                initialActiveIndex={0}
                dataTestId="myAccount"
                items={[
                  {
                    id: 'myOptimaAccount',
                    title: t('common.myOptimaAccount'),
                  },
                ]}
              />
            </div>
            <ProfileCard
              id={loginMemberId}
              imageSrc={
                profileState?.profileImage &&
                'data:image/png;base64,' + profileState?.profileImage
              }
              profileFirstLetter={
                profileState?.profileImage?.length <= 0 && (
                  <UserInitialsAvatar
                    firstName={personalInfo?.user?.firstName || ''}
                    lastName={personalInfo?.user?.lastName || ''}
                  />
                )
              }
              getPhone={getPhone?.benefitPhone1800 ? memberServices : false}
              memberServices={
                getPhone?.benefitPhone1800 ? memberServices : ''
              }
              name={`${toPascalCase(personalInfo?.user?.firstName) || ''} ${toPascalCase(personalInfo?.user?.lastName) || ''}`}
              plan={planList}
              viewDesignatedCallback={navigateViewDesignated}
              showViewDesignated={showSharedAccounts}
              designRepText={t('profile.designatedRepresentative.DRDropDownMenuItem2')}
              planText={t('common.plan')}
              memServiceText={t('common.memberServices')}
              data-testid="account_profile_image"
            />
            <Accordion
              onToggleItem={handleAccordionToggleItem}
              initialActiveIndex={0}
              id="acc_side_menu"
              items={[

                getFeature?.MemberAccount && {
                  accIcon: <></>,
                  id: 'member_profile',
                  content: (
                    <>
                      {profileUrl &&
                        memberProfileUrl.length > 0 && (
                          <DashboardLinks
                            data={memberProfileUrl}
                            showContent={getFeature?.MemberAccount}
                          />
                        )}
                    </>
                  ),
                  title: t('common.memberAccount'),
                },
                //Benefist coverage for for member
                getFeature?.BenefitsCoverage &&
                  isPersonalInfoResolved &&
                  !personalInfo?.isGuest && {
                  accIcon: <UmbrellaIcon />,
                  id: 'benefit_coverage',
                  content: (
                    <>
                      {profileUrl &&
                        benefitsUrl.length > 0 && (
                          <DashboardLinks
                            data={benefitsUrl}
                            showContent={getFeature?.BenefitsCoverage}
                          />
                        )}
                    </>
                  ),
                  title: t('common.benefitsCoverage'),
                },

                //To show only enroll and benefits link in side menu for guest user
                isPersonalInfoResolved && personalInfo?.isGuest && !isAvMed() && {
                  accIcon: <UmbrellaIcon />,
                  id: 'benefit_coverage_guest',
                  content: (
                    <>
                      {guestBenefitsUrl.length > 0 && (
                        <DashboardLinks
                          data={guestBenefitsUrl}
                          showContent={getFeature?.BenefitsCoverage}
                        />
                      )}
                    </>
                  ),
                  title: t('common.benefitsCoverage'),
                },
                getFeature?.WellnessPrograms && personalInfo?.isWellnessUser && {
                  accIcon: <AppleIcon />,
                  id: 'get_healthy_guest',
                  content: (
                    <>
                      {profileUrl &&
                        guestWellnessUrl.length > 0 && (
                          <DashboardLinks
                            data={guestWellnessUrl}
                          />
                        )}
                    </>
                  ),
                  title: t('common.getHealthy'),
                },
                //To show the PaymentsBillingAndClaims links in side menu
                getFeature?.PaymentsBillingAndClaims &&
                  isPersonalInfoResolved &&
                  !personalInfo?.isGuest && {
                  accIcon: <MoneyIcon />,
                  id: 'payment_billing',
                  content: (
                    <>
                      {profileUrl &&
                        paymentCalims.length > 0 && (
                          <DashboardLinks 
                          data={paymentCalims} 
                          />
                        )}
                    </>
                  ),
                  title: t('common.paymentsBillingsClaims'),
                },
                //To show the docotor and Medication link in side menu
                getFeature?.DoctorAndMedications &&
                  isPersonalInfoResolved &&
                  !personalInfo?.isGuest && {
                  accIcon: <StethoscopeIcon />,
                  id: 'doctors_medication',
                  content: (
                    <>
                      {profileUrl && <DashboardLinks 
                      data={doctorsMedication} 
                      />}
                    </>
                  ),
                  title: t('common.doctorsMedication'),
                },
                //To show the gethealthy link in side menu
                isPersonalInfoResolved &&
                shouldDisplayGetHealthyMenu(personalInfo?.isGuest, false, '', getHealthy, getFeature?.GetHealthy) && {
                  accIcon: <AppleIcon />,
                  id: 'get_healthy_guest',
                  content: (
                    <>
                      {profileUrl && <DashboardLinks 
                       data={getHealthy} 
                       />}
                    </>
                  ),
                  title: t('common.getHealthy'),
                },
              ].filter((item) => item)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default SideMenu;